import React, { FC, ReactElement, ReactNode } from 'react';

import { Box, Typography, styled } from '@mui/material';
import { Link } from 'react-router-dom';

import LockIcon from '~/assets/icons/LockIcon';
import AnimatedSkeleton from '~/components/AnimatedSkeleton';
import AnimatedSkeletonMultiline from '~/components/AnimatedSkeletonMultiline';
import CustomButton from '~/components/UI/CustomButton/CustomButton';
import { useTenantContext } from '~/context/TenantProvider';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import { CssUtilityType } from '~/styled';
import {
  MIDDLE_MARGIN_PX,
  SKELETON_TEXT_HEIGHT_BIG_PX,
  SKELETON_TEXT_HEIGHT_PX,
  SKELETON_TEXT_HEIGHT_SMALL_PX,
  SMALL_MARGIN_PX,
  SUBMIDDLE_MARGIN_PX,
  TINY_MARGIN_PX,
  useIsWidthUp,
} from '~/theme';
import { TenantsRefs, TENANTS_CONFIG } from '~/utils/tenantsConfig';

export interface JoinNowBlockProps {
  className?: string;
  verticalSkeleton?: boolean;
  title?: string;
  description?: string | JSX.Element;
  btnCaption?: string;
  additionalCss?: {
    lockTextCss?: CssUtilityType;
    descriptionCss?: CssUtilityType;
    lockIconCss?: CssUtilityType;
    linkCss?: CssUtilityType;
  };
  Icon?: (props: any) => ReactElement;
  loading?: boolean;
  skeletonLines?: number;
  logo?: ReactNode;
  linkProps?: {
    to: string;
    target?: string;
  };
}

const JoinNowBlock: FC<JoinNowBlockProps> = ({
  className,
  loading,
  verticalSkeleton,
  additionalCss,
  title,
  description,
  btnCaption,
  skeletonLines = 3,
  Icon = LockIcon,
  linkProps,
  logo,
  ...rest
}) => {
  const { tenant } = useTenantContext();
  const { t } = useTenantTranslation();
  const joinNowBlockConfig = TENANTS_CONFIG[tenant?.name || TenantsRefs.Fu]?.joinNowBlock;
  const isMobile = !useIsWidthUp('md');

  if (loading) {
    return (
      <LoadingWrapper className={className} data-testid={rest['data-testid']}>
        {verticalSkeleton ? (
          <LoadingContent>
            <LoadingTitle
              xs={SKELETON_TEXT_HEIGHT_PX}
              lines={2}
              spacer={TINY_MARGIN_PX}
              lastLineWidth={70}
            />
            <LoadingDescription
              xs={SKELETON_TEXT_HEIGHT_SMALL_PX}
              lines={5}
              spacer={TINY_MARGIN_PX}
            />
            <AnimatedSkeleton xs={SKELETON_TEXT_HEIGHT_BIG_PX} />
          </LoadingContent>
        ) : (
          <>
            {!isMobile && (
              <LoadingHorizontalTitle
                xs={SKELETON_TEXT_HEIGHT_SMALL_PX}
                lines={2}
                spacer={TINY_MARGIN_PX}
                lastLineWidth={100}
              />
            )}
            <LoadingHorizontalDescription
              xs={SKELETON_TEXT_HEIGHT_SMALL_PX}
              lines={skeletonLines}
              spacer={TINY_MARGIN_PX}
            />
            <LoadingButton xs={SKELETON_TEXT_HEIGHT_BIG_PX} />
          </>
        )}
      </LoadingWrapper>
    );
  }

  return (
    <Wrapper className={className} data-testid={rest['data-testid']}>
      <StyledIcon as={Icon} additionalStyles={additionalCss?.lockIconCss} />
      <LockText variant="h2" additionalStyles={additionalCss?.lockTextCss}>
        {title || t(joinNowBlockConfig?.titleKey)}
      </LockText>
      {typeof description === 'string' || typeof description === 'undefined' ? (
        <Description additionalStyles={additionalCss?.descriptionCss}>
          {description || t(joinNowBlockConfig?.descriptionKey)}
        </Description>
      ) : (
        description
      )}
      <StyledLink
        to={linkProps?.to || joinNowBlockConfig?.link}
        target={linkProps?.target}
        data-testid="joinNowLink"
        additionalStyles={additionalCss?.linkCss}
      >
        <CustomButton>{btnCaption || t(joinNowBlockConfig?.btnCaptionKey)}</CustomButton>
      </StyledLink>
    </Wrapper>
  );
};

export default JoinNowBlock;

const LoadingTitle = styled(AnimatedSkeletonMultiline)`
  margin-bottom: ${SUBMIDDLE_MARGIN_PX};
`;
const LoadingHorizontalTitle = styled(AnimatedSkeletonMultiline)`
  flex-grow: 1;
  width: 100%;
  margin: auto 0 ${SMALL_MARGIN_PX};

  ${({ theme }) => theme.breakpoints.up('md')} {
    width: unset;
    margin: auto 0;
  }
`;
const LoadingContent = styled('div')`
  width: 100%;
`;
const LoadingDescription = styled(AnimatedSkeletonMultiline)`
  margin-bottom: ${SUBMIDDLE_MARGIN_PX};
`;
const LoadingHorizontalDescription = styled(AnimatedSkeletonMultiline)`
  flex-grow: 5;
  margin-bottom: ${SMALL_MARGIN_PX};
  width: 100%;

  ${({ theme }) => theme.breakpoints.up('md')} {
    margin: 0 ${MIDDLE_MARGIN_PX};
    width: unset;
  }
`;
const Wrapper = styled(Box)`
  border-radius: 10px;
  padding: 24px 24px 24px 72px;
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  background: ${({ theme }) => theme.palette.common.card.background.light};
  box-shadow: ${({ theme }) => theme.palette.common.card.shadowValue};
  position: relative;
  align-items: flex-start;

  ${({ theme }) => theme.breakpoints.up('md')} {
    flex-direction: row;
    padding: 32px ${MIDDLE_MARGIN_PX};
    align-items: center;
  }
`;
const LoadingButton = styled(AnimatedSkeleton)`
  width: 100%;
  flex-grow: 1;
  min-width: 200px;
  margin: 0 auto;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    width: unset;
  }

  ${({ theme }) => theme.breakpoints.up('md')} {
    margin: unset;
  }
`;
const LoadingWrapper = styled(Wrapper)`
  padding-left: ${SUBMIDDLE_MARGIN_PX};
`;
const StyledIcon = styled('div', {
  shouldForwardProp: (prop) => prop !== 'additionalStyles' && prop !== 'as',
})<{ additionalStyles?: CssUtilityType }>`
  width: 24px;
  height: 24px;
  position: absolute;
  top: 28px;
  left: 26px;

  ${({ theme }) => theme.breakpoints.up('md')} {
    position: relative;
    margin-right: 25px;
    width: 43px;
    height: 43px;
    top: unset;
    left: unset;
  }

  ${({ additionalStyles }) => additionalStyles}
`;
const LockText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'additionalStyles',
})<{ additionalStyles?: CssUtilityType }>`
  && {
    font-size: 18px;
    line-height: 1.2;
    margin-bottom: 10px;

    ${({ theme }) => theme.breakpoints.up('md')} {
      font-size: 20px;
      margin-bottom: 10px;
      width: 130px;
    }

    ${({ additionalStyles }) => additionalStyles}
  }
`;
const Description = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'additionalStyles',
})<{ additionalStyles?: CssUtilityType }>`
  flex: 4;
  color: ${({ theme }) => theme.palette.common.white};
  font-size: 16px;
  line-height: 1.5;
  margin-bottom: 15px;

  ${({ theme }) => theme.breakpoints.up('md')} {
    text-align: left;
    font-size: 18px;
    margin-right: 90px;
    margin-left: 48px;
    margin-bottom: 0;
  }

  ${({ additionalStyles }) => additionalStyles}
`;
const StyledLink = styled(Link, {
  shouldForwardProp: (prop) => prop !== 'additionalStyles',
})<{ additionalStyles?: CssUtilityType }>`
  width: 100%;

  & button {
    width: 100%;
  }

  ${({ theme }) => theme.breakpoints.up('sm')} {
    width: auto;
    margin: 0 auto;
  }

  ${({ theme }) => theme.breakpoints.up('md')} {
    margin: 0;
  }

  ${({ additionalStyles }) => additionalStyles}
`;
